import { TierIIReportKind } from "encamp-prisma/generated/client/client";
import { TabProxy } from "encampy";
import {
  AlternateId,
  PortalCredential,
  PortalType,
} from "generated-graphql/graphql";
import { automateAZmyDEQLogin } from "./az";
import { automateCERSLogin } from "./ca";
import { automateEPlanLogin } from "./eplan";
import { automateMOLogin } from "./mo";
import { automateTierIIManagerLogin } from "./tieriimanager";
import { automateTxLogin } from "./tx";

export function getEncampyLoginAutomation(
  portalCredential: PortalCredential,
  facilityAlternateIds?: AlternateId[] | null,
  reportingYear?: number,
  reportKind?: TierIIReportKind
): (tab: TabProxy) => Promise<void> {
  if (!portalCredential.type) {
    return () => {
      throw new Error("Portal type is required for login automation");
    };
  }

  if (!portalCredential.url) {
    return () => {
      throw new Error("Portal URL is required for login automation");
    };
  }

  if (
    !portalCredential.credential?.username ||
    !portalCredential.credential?.password
  ) {
    return () => {
      throw new Error(
        "Username and password are required for login automation"
      );
    };
  }

  if (
    portalCredential.type === PortalType.Tx &&
    !portalCredential.credential?.securityQuestions
  ) {
    return () => {
      throw new Error(
        "Security questions are required for Texas portal login automation"
      );
    };
  }

  const { username, password, securityQuestions } = portalCredential.credential;

  switch (portalCredential.type) {
    case PortalType.AZmyDeq:
      return (tab: TabProxy) => automateAZmyDEQLogin(tab, username, password);

    case PortalType.Cers:
      return (tab: TabProxy) =>
        automateCERSLogin({
          tab,
          username,
          password,
          facilityAlternateIds: facilityAlternateIds ?? [],
        });

    case PortalType.EPlan:
      return (tab: TabProxy) => automateEPlanLogin(tab, username, password);

    case PortalType.Mo:
      return (tab: TabProxy) => automateMOLogin(tab, username, password);

    case PortalType.Tx:
      return (tab: TabProxy) =>
        automateTxLogin({
          tab,
          username,
          password,
          facilityAlternateIds: facilityAlternateIds ?? [],
          securityQuestions: securityQuestions ?? [],
          reportingYear,
          reportKind,
        });

    case PortalType.Tier2Manager:
      return (tab: TabProxy) =>
        automateTierIIManagerLogin(tab, username, password);

    // Add cases for other portal types as needed
    default:
      return () => {
        throw new Error(
          `Login automation not implemented for portal type: ${portalCredential.type}`
        );
      };
  }
}
